/**
 * Phreatic System Flow Diagram — Complete Code-1:1 Architecture
 *
 * Direct visual representation of how phreatic actually works:
 *   - 5 ingestion sources (snip, CSV, plan well, manual, telemetry STANDBY)
 *   - 13 DB tables grouped by purpose (core / V5 outputs / telemetry)
 *   - V5 aquifer-routed predictor — called from 4 distinct paths:
 *       1. /calculator/auto-assess (the main workhorse)
 *       2. /sites/{id}/generate-predictions (writes to PhreaticPrediction)
 *       3. /v4/predict/batch (hydrograph forward overlay)
 *       4. /pump-control/schedule/generate (inline future_sat for scheduler)
 *   - Site Assessment orchestrator that fans calcData to all tabs
 *   - 13 user-facing tabs (each is its own calculator)
 *   - Pump Optimizer 4-step pipeline (matrix → schedule → simulate → recs)
 *   - Telemetry ingestion bridge with 5 anomaly rules (when active)
 *   - Blue Diamond statewide V5 precompute (separate product line)
 *   - 5 feedback loops shown as thick ⇒ arrows
 */
import React, { useEffect, useRef, useState } from 'react';

const DIAGRAM = `
flowchart TB
  %% ═══════════════════════════════════════
  %% DATA INGESTION
  %% ═══════════════════════════════════════
  subgraph INGEST["Data Ingestion"]
    direction TB
    SNIP["Command Center Snip\\n(draws property + reference bbox)"]
    CSV["CSV Import\\n(Well Inventory tab)\\n+ Gemini AI parser"]
    PLANW["Plan Well button\\n(Well Intelligence tab)"]
    MANUAL["Manual Gauging\\n(Well Inventory form)"]
    TELEM["Telemetry Adapters (STANDBY)\\nAqvify / HOBOnet / MQTT / Manual"]
  end

  %% ═══════════════════════════════════════
  %% STORAGE — CORE
  %% ═══════════════════════════════════════
  subgraph DB_CORE["DB — Site & Well Tables"]
    direction TB
    PSITE["PhreaticSite\\n(snipped sites + bounds + acreage)"]
    PSW["PhreaticSiteWell ⇄ TwdbWell\\n(junction to V5-eligible wells)"]
    TWDB["TwdbWell\\n(47K V5 training corpus —\\nwells with multi-gauging history)"]
    TWL["TwdbWaterLevel\\n(historical gaugings for TwdbWell)"]
    BDW["BlueDiamondWell\\n(124K statewide display wells\\nshown on snip preview only)"]
    PA["PumpAsset\\n(imported + Plan Well wells)"]
    WG["WellGauging\\n(asset gaugings + manual + telemetry)"]
  end

  %% ═══════════════════════════════════════
  %% STORAGE — PREDICTIONS / SCHEDULES
  %% ═══════════════════════════════════════
  subgraph DB_OUT["DB — V5 Outputs & Schedules"]
    direction TB
    PHPRED["PhreaticPrediction\\n(stored LSTM forecasts —\\nfrom /generate-predictions)"]
    PSCHED["PumpSchedule\\n(optimizer output JSONB —\\nstatus: active / superseded)"]
    BDG["blue_diamond_grids\\n(precomputed per-aquifer × horizon)"]
  end

  %% ═══════════════════════════════════════
  %% STORAGE — TELEMETRY (mostly dormant)
  %% ═══════════════════════════════════════
  subgraph DB_TELEM["DB — Telemetry (when active)"]
    direction TB
    TR["TelemetryReading\\n(every reading from any source)"]
    PCMD["PumpCommand\\n(audit log of every command)"]
    TA["TelemetryAlert\\n(5 anomaly types persisted)"]
  end

  %% Ingestion → Storage
  SNIP -->|"creates PhreaticSite"| PSITE
  SNIP -->|"links V5-eligible wells"| PSW
  SNIP -.->|"display only on preview\\n(no junctions, no inference)"| BDW
  PSW -.->|"FK references"| TWDB
  TWDB -.->|"history"| TWL
  CSV -->|"creates PumpAsset"| PA
  CSV -->|"bridges gaugings"| WG
  PLANW -->|"creates planned PumpAsset"| PA
  MANUAL -->|"ingest_manual_gauging()"| WG
  MANUAL -->|"updates current_dtw"| PA
  TELEM -.->|"(when active)\\ningest_reading()"| TR
  TELEM -.->|"(when active)\\nbridges WellGauging"| WG
  TELEM -.->|"(when active)\\nupdates current_dtw"| PA

  %% ═══════════════════════════════════════
  %% V5 INFERENCE — AQUIFER-ROUTED
  %% ═══════════════════════════════════════
  subgraph V5["V5 Predictor (aquifer-routed)\\ncontrols: aquifer_filter, gauging_recency_years, available_aquifers"]
    direction TB
    ROUTER{{"Aquifer Router\\n(by well.aquifer_name)"}}

    subgraph OG["Ogallala bundle"]
      OG_ALL["LSTM + XGB×6 + LGB×6 + CAT×6\\n→ Ridge meta-blend"]
    end
    subgraph EDW["Edwards bundle"]
      EDW_ALL["LSTM + XGB×6 + LGB×6 + CAT×6\\n→ Ridge meta-blend"]
    end
    subgraph GC["Gulf Coast bundle"]
      GC_ALL["LSTM + XGB×6 + LGB×6 + CAT×6\\n→ Ridge meta-blend"]
    end
    subgraph GEN["General bundle (default)"]
      GEN_ALL["LSTM + XGB×6 + LGB×6 + CAT×6\\n→ Ridge meta-blend"]
    end

    HORIZONS["6 Horizons\\n3mo | 1yr | 2yr | 5yr | 10yr | 20yr\\n(p10 / p50 / p90 quantiles from LSTM)"]

    ROUTER -->|"Ogallala"| OG_ALL
    ROUTER -->|"Edwards"| EDW_ALL
    ROUTER -->|"Gulf Coast"| GC_ALL
    ROUTER -->|"else"| GEN_ALL
    OG_ALL --> HORIZONS
    EDW_ALL --> HORIZONS
    GC_ALL --> HORIZONS
    GEN_ALL --> HORIZONS
  end

  %% V5 inputs — measurements get routed by aquifer name
  TWL -->|"reference well measurements"| ROUTER
  WG -->|"imported well measurements\\n(3+ readings required)"| ROUTER

  %% ═══════════════════════════════════════
  %% SITE ASSESSMENT — THE WORKHORSE ORCHESTRATOR
  %% Frontend "Run Assessment" → /calculator/auto-assess
  %% ═══════════════════════════════════════
  SA["Site Assessment Overhead Calc\\n/calculator/auto-assess\\n• merges PSW+TwdbWell + PumpAsset + MonitoringWell wells\\n• filters by aquifer_filter & gauging_recency_years\\n• runs V5 per well via Router\\n• chains every tab calculator\\n• applies Floyd post-processor\\n• returns calcData (one fat object)"]

  %% Three well sources merge into auto-assess
  PSW -->|"reference TWDB set"| SA
  PA -->|"imported PumpAsset set"| SA
  HORIZONS -->|"V5 predictions per well"| SA

  USECASE["Use Case + Scale + Pump Hours\\n(15 use cases — cattle / corn / dairy /\\ndata center / municipal / oil&gas / …)\\n→ computedGpm + computedAnnualAF\\n+ optional measuredGpm override"]
  USECASE -->|"demand inputs"| SA

  FLOYD["Floyd Factor (post-processor)\\n• tier classification (>300 / >100 / >40 / <40 ft)\\n• economic_volume vs floyd_zone split\\n• decline acceleration formula <40ft\\n• inference_decline (V5) vs historical_decline\\n⚠ DUPLICATED in 4 files w/ varying thresholds"]
  SA -->|"decorates outputs"| FLOYD

  %% ═══════════════════════════════════════
  %% USER TABS — each tab IS its own calculator
  %% ═══════════════════════════════════════
  subgraph TABS["User Tabs (each = a calculator)"]
    direction TB

    subgraph MAIN["Main row"]
      direction TB
      T_HYDRO["Hydrograph\\n• PSW historical + PA timeseries\\n• V5 forward overlay (p10/p50/p90)"]
      T_3D["3D Well Table\\n• Three.js mesh + client IDW\\n• time-scrub via predictedWells"]
      T_MAP["Well Map (2D)\\n• Leaflet + contour lines\\n• gradient interpolation"]
      T_XSEC["Cross-Section\\n• two-point IDW slice\\n+ /v4/geology endpoint"]
      T_VOL["Volume\\n• V = S(aquifer) × A(snip) × avg_sat(IDW)\\n• Floyd splits economic vs floyd-zone"]
      T_ECON["Economics\\n4 parallel calls:\\n• /calculator/demand-by-use (calc 11)\\n• /calculator/water-value (calc 12)\\n• /calculator/cost-product-ratio (calc 13)\\n• /calculator/crop-tradeoff (calc 14, ag only)\\n+ /calculator/crop-prices for chart"]
      T_PUMPRATE["Pump Rate\\n• calc_sustainable_rate (calc 5)\\n• max GPM holding sat above threshold"]
      T_REPORT["Report\\n• generate_report_data (calc 10)\\n• aggregator over every calc above"]
    end

    subgraph WOPS["Well Operations"]
      direction TB
      T_INV["Well Inventory\\n• /pump-control/assets (CRUD)\\n• /assets/parse-csv (Gemini AI)\\n• /assets/import-csv (bulk write)\\n• /assets/{id}/gaugings\\n• /telemetry/manual"]
      T_WELLINT["Well Intelligence\\n• /calculator/well-installation (calc 13)\\n• grid scoring: future_sat × theis_penalty × distance\\n• greedy ROI exclusion → drilling recs\\n• /pump-control/assets POST = Plan Well"]
      T_PUMP["Pump Control\\n• /pump-control/schedule/generate\\n  (calls V5 inline for future_sat per well)\\n• /pump-control/interference (matrix only)\\n• /pump-control/command/send (audit)"]
      T_CAVIT["Cavitation\\n• /v4/cavitation (calls compute_cavitation_timeline)\\n• /assets/{site}/gaugings-by-name\\n• per-well risk 3mo→20yr"]
      T_CONE["Cone of Depression\\n• Theis Q = use_case_gpm / PA_count\\n• PumpAssets ONLY (no reference wells)\\n• T = K(aquifer) × sat — superposed grid"]
    end
  end

  %% calcData fans out to every tab
  SA -->|"calcData → assessment"| T_HYDRO
  SA -->|"calcData"| T_3D
  SA -->|"calcData"| T_MAP
  SA -->|"calcData"| T_XSEC
  SA -->|"calcData → volume"| T_VOL
  SA -->|"calcData → economics base"| T_ECON
  SA -->|"calcData → sustainable_rate"| T_PUMPRATE
  SA -->|"calcData → all sections"| T_REPORT
  SA -->|"calcData → predictions"| T_INV
  SA -->|"calcData → well_placement"| T_WELLINT
  SA -->|"calcData → pump schedule inputs"| T_PUMP
  SA -->|"calcData → cavitation timeline"| T_CAVIT

  %% Direct DB reads & specialized backend calls (in addition to calcData)
  HORIZONS -.->|"V5 forward overlay\\nvia /v4/predict/batch\\n(p10/p50/p90 bands)"| T_HYDRO
  PSW -.->|"bundle.timeseries + bundle.wells"| T_HYDRO
  PSW -.->|"reference well points"| T_3D
  PSW -.->|"contour generation"| T_MAP
  PA -.->|"asset-timeseries\\n(merged colored lines)"| T_HYDRO
  PA -.->|"importedWellPoints\\n(merged into currentWells)"| T_3D
  PA -.->|"importedWellPoints"| T_MAP
  PA -.->|"PumpAsset CRUD"| T_INV
  PA -.->|"active PumpAssets ONLY"| T_CONE
  PA -.->|"PumpAssets to schedule"| T_PUMP
  PA -.->|"PumpAssets for cavitation"| T_CAVIT
  WG -.->|"per-well gauging history"| T_CAVIT
  WG -.->|"per-asset gauging history"| T_INV

  %% ═══════════════════════════════════════
  %% PUMP OPTIMIZER PIPELINE (inside Pump Control tab)
  %% Two distinct routes both surface in Pump Control:
  %%   1. /pump-control/schedule/generate — calls V5 inline + writes PumpSchedule
  %%   2. /v4/pump-optimizer — older route, no DB write
  %% ═══════════════════════════════════════
  subgraph PUMPOPT_PIPE["Pump Optimizer Pipeline (4 steps)"]
    direction TB
    PO_INTF["Step 1\\nbuild_interference_matrix\\n(NxN Theis drawdown)"]
    PO_SCHED["Step 2\\ngenerate_schedule\\n(heuristic conflict-graph scheduler\\n+ Floyd-tier hour caps)"]
    PO_SIM["Step 3\\nsimulate_schedule\\n(replay through Theis,\\n15-min timesteps × 24hr)"]
    PO_RECS["Step 4\\nrecommendations\\n(stagger pairs / reduce demand /\\nFloyd warnings per well)"]
    PO_INTF --> PO_SCHED --> PO_SIM --> PO_RECS
  end
  T_PUMP -->|"runs"| PO_INTF
  HORIZONS -.->|"future_sat per well\\n(V5 called inline by\\nschedule/generate route)"| PO_SCHED
  PO_RECS -->|"writes (status=active,\\nsupersedes prior)"| PSCHED
  T_PUMP -.->|"command audit"| PCMD

  %% ═══════════════════════════════════════
  %% V5 INFERENCE PATHS — 4 distinct entry points
  %% (the V5 router is called from multiple places, not just SA)
  %% ═══════════════════════════════════════
  SA -.->|"V5 path 1:\\nauto-assess merge"| ROUTER
  GENPRED["/sites/{id}/generate-predictions\\n(LSTM-only, stored)"]
  GENPRED -->|"V5 path 2:\\nstandalone forecast"| ROUTER
  GENPRED -->|"writes stored predictions"| PHPRED
  T_HYDRO -.->|"V5 path 3:\\n/v4/predict/batch (forward overlay)"| ROUTER
  PO_SCHED -.->|"V5 path 4:\\ninline future_sat per well"| ROUTER

  %% ═══════════════════════════════════════
  %% TELEMETRY INGESTION (single bridge, mostly dormant)
  %% ═══════════════════════════════════════
  subgraph TELEM_BRIDGE["Telemetry Ingestion (telemetry/ingestion.py)"]
    direction TB
    TI_INGEST["ingest_reading()\\n• 1. Write TelemetryReading\\n• 2. Bridge to WellGauging\\n• 3. Update PumpAsset.current_dtw_ft\\n• 4. Run anomaly detection (5 rules)\\n• 5. Persist alerts"]
    TI_RULES["5 anomaly rules:\\n• rapid_level_change (>5ft delta)\\n• pump_no_flow (cavitation risk)\\n• neighbor_interference (off but dropping)\\n• low_battery (<20%)\\n• floyd_tier_crossing"]
    TI_INGEST --> TI_RULES
  end
  TELEM -.->|"all sources\\nfunnel here"| TI_INGEST
  TI_RULES -.->|"persist"| TA

  %% ═══════════════════════════════════════
  %% BLUE DIAMOND — STATEWIDE V5 PRECOMPUTE
  %% (separate product line, separate frontend)
  %% ═══════════════════════════════════════
  subgraph BD["Blue Diamond (Statewide V5 Heatmap)"]
    direction TB
    BD_PRECOMP["precompute_blue_diamond_v5.py\\n• loads 47K twdb_wells (training corpus)\\n• runs V5 per well via Router\\n• IDW per aquifer × per horizon\\n• writes 9 per-aquifer + 1 blended grid"]
    BD_TAB["BlueDiamond.tsx\\n• /grid?aquifer=X&horizon=Y (heatmap)\\n• /wells (raw 124K points)\\n• /stats (per-county + per-aquifer aggregates)"]
  end
  TWDB -->|"47K training-corpus wells"| BD_PRECOMP
  ROUTER -->|"V5 predictions per well"| BD_PRECOMP
  BD_PRECOMP -->|"writes precomputed grids"| BDG
  BDG -->|"reads grids for heatmap"| BD_TAB
  BDW -.->|"raw points & stats endpoints"| BD_TAB

  %% ═══════════════════════════════════════
  %% FEEDBACK LOOPS — backward edges (thick ⇒ for visibility)
  %% These are the 5 cascades that re-trigger inference
  %% ═══════════════════════════════════════
  T_INV ==>|"feedback 1: gauging entry"| WG
  T_INV ==>|"feedback 2: telemetry config"| PA
  T_WELLINT ==>|"feedback 3: Plan Well → new PumpAsset"| PA
  T_PUMP ==>|"feedback 4: schedule commands\\n(when telemetry active)"| TELEM
  WG ==>|"feedback 5: any new gauging\\n→ V5 re-prediction on next /auto-assess\\n→ all tabs refresh"| ROUTER

  %% ═══════════════════════════════════════
  %% CONFIG PERSISTENCE
  %% ═══════════════════════════════════════
  CFG["Site config\\n(use case, scale, pump_hrs, parcels)\\nstored on PhreaticSite.bounds._config"]
  USECASE -->|"Run & Save"| CFG
  CFG -->|"loads on mount"| USECASE

  %% ═══════════════════════════════════════
  %% STYLING
  %% ═══════════════════════════════════════
  classDef ingestion fill:#0a2a3a,stroke:#06b6d4,color:#cffafe
  classDef db fill:#0a2a3a,stroke:#06b6d4,color:#cffafe
  classDef dbout fill:#1a1a3a,stroke:#7dd3fc,color:#e0f2fe
  classDef dbtelem fill:#1a1a1a,stroke:#64748b,color:#cbd5e1,stroke-dasharray: 4 4
  classDef v5 fill:#1f2937,stroke:#fbbf24,color:#fef3c7
  classDef bd fill:#1a1530,stroke:#a855f7,color:#e9d5ff
  classDef sa fill:#0d1f1a,stroke:#10b981,color:#d1fae5
  classDef tab fill:#0a2a3a,stroke:#06b6d4,color:#cffafe
  classDef floyd fill:#2a1a0a,stroke:#fb923c,color:#ffedd5
  classDef pumpopt fill:#1a2a1a,stroke:#84cc16,color:#ecfccb
  classDef telem fill:#1a1a1a,stroke:#64748b,color:#94a3b8,stroke-dasharray: 4 4
  classDef standby fill:#1a1a1a,stroke:#64748b,color:#94a3b8,stroke-dasharray: 4 4
  classDef config fill:#0a2a3a,stroke:#06b6d4,color:#cffafe

  class SNIP,CSV,PLANW,MANUAL ingestion
  class TELEM standby
  class PSITE,PSW,TWDB,TWL,BDW,PA,WG db
  class PHPRED,PSCHED,BDG dbout
  class TR,PCMD,TA dbtelem
  class ROUTER,OG_ALL,EDW_ALL,GC_ALL,GEN_ALL,HORIZONS,GENPRED v5
  class BD_PRECOMP,BD_TAB bd
  class SA,USECASE sa
  class FLOYD floyd
  class T_HYDRO,T_3D,T_MAP,T_XSEC,T_VOL,T_ECON,T_PUMPRATE,T_REPORT,T_INV,T_WELLINT,T_PUMP,T_CAVIT,T_CONE tab
  class PO_INTF,PO_SCHED,PO_SIM,PO_RECS pumpopt
  class TI_INGEST,TI_RULES telem
  class CFG config

  %% Default arrow style
  linkStyle default stroke:#06b6d4,stroke-width:1px
`;

/**
 * Phreatic ML Engine + CARI Wire — 1:1 transcription of the hand-drawn wire.
 *   Left  — ingestion DB (USGS / TWDB / GCD) → KNN reclassify + DD dedup,
 *           fanned out to the per-aquifer rows.
 *   Middle— the ML engine grid: ensemble (lstm + xgb, light, cat) across
 *           horizons (3m | 1y | 2y | 5y | 10y | 20y) × 11 aquifer bundles.
 *   Right — CARI LLM call, context-injected by (a) the engine's per-aquifer/
 *           horizon predictions and (b) the Vector DB (GW bible + Reg).
 */
const WIRE_DIAGRAM = `
flowchart LR
  %% ═══════════════════════════════════════
  %% LEFT — DATA SOURCES → KNN + DEDUP ROUTING
  %% ═══════════════════════════════════════
  subgraph SRC["DB"]
    direction TB
    USGS["USGS"]
    TWDB_W["TWDB"]
    GCD["GCD"]
  end

  KNN{{"KNN + DD\\n(reclassify + dedup)"}}
  USGS --> KNN
  TWDB_W --> KNN
  GCD --> KNN

  %% ═══════════════════════════════════════
  %% MIDDLE — ML ENGINE GRID (aquifer × horizon)
  %% ═══════════════════════════════════════
  subgraph ENGINE["ML Engine&nbsp;&nbsp;lstm + xgb, light, cat\\nhorizons:&nbsp;&nbsp;3m | 1y | 2y | 5y | 10y | 20y"]
    direction TB
    A_OG["Ogallala"]
    A_EDW["Edwards"]
    A_GULF["Gulf"]
    A_DOCK["Dockum"]
    A_TRIN["Trinity"]
    A_WIL["Wilcox"]
    A_SEY["Seymour"]
    A_MES["Mesilla"]
    A_PVA["PVA"]
    A_RUST["Rustler"]
    A_GEN["General"]
  end

  %% KNN fans routed wells out to each aquifer bundle (the spider lines)
  KNN -->|"routed wells"| A_OG
  KNN --> A_EDW
  KNN --> A_GULF
  KNN --> A_DOCK
  KNN --> A_TRIN
  KNN --> A_WIL
  KNN --> A_SEY
  KNN --> A_MES
  KNN --> A_PVA
  KNN --> A_RUST
  KNN --> A_GEN

  %% ═══════════════════════════════════════
  %% RIGHT — CARI LLM CALL + VECTOR DB (RAG)
  %% ═══════════════════════════════════════
  CARI["Cari LLM call"]

  subgraph VDB["Vector DB"]
    direction TB
    GWB["GW bible"]
    REG["Reg"]
  end

  %% Both braces feed context into the CARI call (the two hatched injection points)
  ENGINE -->|"predictions\\n(context injection)"| CARI
  VDB -->|"GW bible + Reg\\n(context injection)"| CARI

  %% ═══════════════════════════════════════
  %% STYLING (matches system diagram palette)
  %% ═══════════════════════════════════════
  classDef src fill:#0a2a3a,stroke:#06b6d4,color:#cffafe
  classDef knn fill:#1f2937,stroke:#fbbf24,color:#fef3c7
  classDef engine fill:#1f2937,stroke:#fbbf24,color:#fef3c7
  classDef cari fill:#0d1f1a,stroke:#10b981,color:#d1fae5
  classDef vdb fill:#1a1530,stroke:#a855f7,color:#e9d5ff

  class USGS,TWDB_W,GCD src
  class KNN knn
  class A_OG,A_EDW,A_GULF,A_DOCK,A_TRIN,A_WIL,A_SEY,A_MES,A_PVA,A_RUST,A_GEN engine
  class CARI cari
  class GWB,REG vdb

  linkStyle default stroke:#06b6d4,stroke-width:1px
`;

export function SystemFlowDiagram() {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgNodeRef = useRef<HTMLDivElement | null>(null);
  const wireContainerRef = useRef<HTMLDivElement>(null);
  const wireSvgNodeRef = useRef<HTMLDivElement | null>(null);
  const [rendered, setRendered] = useState(false);
  const [wireRendered, setWireRendered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      let mermaid: any;
      try {
        mermaid = await import('mermaid');
        mermaid = mermaid.default || mermaid;
      } catch {
        try {
          await new Promise<void>((resolve, reject) => {
            if ((window as any).mermaid) { resolve(); return; }
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load mermaid from CDN'));
            document.head.appendChild(script);
          });
          mermaid = (window as any).mermaid;
        } catch (err: any) {
          if (!cancelled) setError(err.message);
          return;
        }
      }

      if (cancelled || !containerRef.current) return;

      mermaid.initialize({
        startOnLoad: false,
        theme: 'dark',
        themeVariables: {
          darkMode: true,
          primaryColor: '#06b6d4',
          primaryTextColor: '#e2e8f0',
          primaryBorderColor: '#334155',
          lineColor: '#06b6d4',
          secondaryColor: '#1e293b',
          tertiaryColor: '#0f172a',
          background: '#0a0a0a',
          mainBkg: '#1e293b',
          nodeBorder: '#334155',
          clusterBkg: '#0f172a',
          clusterBorder: '#1e293b',
          titleColor: '#e2e8f0',
          edgeLabelBackground: '#1e293b',
          fontSize: '12px',
        },
        flowchart: {
          htmlLabels: true,
          curve: 'basis',
          padding: 15,
          nodeSpacing: 40,
          rankSpacing: 50,
        },
      });

      try {
        // Render into a detached element to avoid React DOM conflicts
        const { svg } = await mermaid.render('phreatic-flow-' + Date.now(), DIAGRAM);
        if (!cancelled && containerRef.current) {
          // Create a child div outside React's management
          if (svgNodeRef.current) {
            svgNodeRef.current.remove();
          }
          const svgDiv = document.createElement('div');
          svgDiv.style.position = 'relative';
          svgDiv.style.zIndex = '10';
          svgDiv.innerHTML = svg;
          svgNodeRef.current = svgDiv;
          containerRef.current.appendChild(svgDiv);
          setRendered(true);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to render diagram');
      }

      try {
        // Second diagram — the ML engine + CARI wire
        const { svg } = await mermaid.render('phreatic-wire-' + Date.now(), WIRE_DIAGRAM);
        if (!cancelled && wireContainerRef.current) {
          if (wireSvgNodeRef.current) {
            wireSvgNodeRef.current.remove();
          }
          const svgDiv = document.createElement('div');
          svgDiv.style.position = 'relative';
          svgDiv.style.zIndex = '10';
          svgDiv.innerHTML = svg;
          wireSvgNodeRef.current = svgDiv;
          wireContainerRef.current.appendChild(svgDiv);
          setWireRendered(true);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to render diagram');
      }
    }

    render();
    return () => {
      cancelled = true;
      // Clean up the manually created nodes
      if (svgNodeRef.current) {
        svgNodeRef.current.remove();
        svgNodeRef.current = null;
      }
      if (wireSvgNodeRef.current) {
        wireSvgNodeRef.current.remove();
        wireSvgNodeRef.current = null;
      }
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-1">
          Phreatic System Architecture
        </h3>
        <p className="text-[10px] text-mono-500 mb-4">
          Complete code-1:1 architecture. 5 ingestion sources → 13 DB tables → V5 (aquifer-routed, 4 distinct call paths) → Site Assessment orchestrator → 13 tab calculators. Pump Optimizer Pipeline (4 steps) writes PumpSchedule. Telemetry ingestion bridges to V5 via WellGauging when active. 5 thick ⇒ feedback loops show user actions that re-trigger inference.
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-red-400 text-xs mb-3">
            Diagram rendering failed: {error}
          </div>
        )}

        <div
          ref={containerRef}
          className="overflow-auto bg-mono-950 rounded-lg p-4 min-h-[400px]"
          style={{ maxHeight: '80vh' }}
        >
          {!rendered && !error && (
            <div className="flex items-center justify-center h-64 text-mono-500 text-sm animate-pulse relative z-10">
              Rendering system diagram...
            </div>
          )}
        </div>
      </div>

      {/* ML Engine + CARI wire */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-1">
          ML Engine + CARI Wire
        </h3>
        <p className="text-[10px] text-mono-500 mb-4">
          USGS / TWDB / GCD → KNN reclassify + dedup routing → per-aquifer ensemble grid (lstm + xgb, light, cat across 3m / 1y / 2y / 5y / 10y / 20y horizons) → predictions context-injected into the CARI LLM call, which is also context-injected from the Vector DB (GW bible + Reg).
        </p>

        <div
          ref={wireContainerRef}
          className="overflow-auto bg-mono-950 rounded-lg p-4 min-h-[400px]"
          style={{ maxHeight: '80vh' }}
        >
          {!wireRendered && !error && (
            <div className="flex items-center justify-center h-64 text-mono-500 text-sm animate-pulse relative z-10">
              Rendering ML engine wire...
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h4 className="text-[10px] text-mono-400 uppercase tracking-wider mb-2">Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
          {[
            { label: 'Data Ingestion', color: 'border-cyan-500 bg-[#0a2a3a]' },
            { label: 'DB — Core (sites + wells)', color: 'border-cyan-500 bg-[#0a2a3a]' },
            { label: 'DB — V5 outputs / schedules', color: 'border-sky-300 bg-[#1a1a3a]' },
            { label: 'DB — Telemetry (dormant)', color: 'border-slate-400 bg-[#1a1a1a]' },
            { label: 'V5 Inference', color: 'border-cyan-400 bg-[#1f2937]' },
            { label: 'Site Assessment', color: 'border-cyan-500 bg-[#0d1f1a]' },
            { label: 'Tab Calculators', color: 'border-cyan-500 bg-[#0a2a3a]' },
            { label: 'Floyd Factor', color: 'border-orange-400 bg-[#2a1a0a]' },
            { label: 'Pump Optimizer Pipeline', color: 'border-lime-500 bg-[#1a2a1a]' },
            { label: 'Telemetry Ingestion', color: 'border-slate-400 bg-[#1a1a1a]' },
            { label: 'Blue Diamond', color: 'border-purple-400 bg-[#1a1530]' },
          ].map(({ label, color }) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded border ${color}`} />
              <span className="text-mono-400">{label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <span className="text-cyan-500">→ data flow</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-cyan-300">⇒ feedback loop</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-mono-500">⋯ optional / standby</span>
          </div>
        </div>
      </div>
    </div>
  );
}
